import { FormEvent, useState } from 'react'

import * as api from 'api/api'
import { CatalogItem, CatalogKind } from 'api/types'
import { useConfirm } from 'components/Confirm'
import { IconName } from 'components/Icon'
import { useProject } from 'components/Layout'
import { Button, Card, EmptyState, ErrorState, IconButton, Modal, Sheet, Spinner, TextField } from 'components/ui'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'

/** Shared CRUD shell for a single-kind reference list (Figma: Equipment/Materials catalogue + "Add" sheet). */
export const CataloguePage = ({
  kind,
  title,
  description,
  icon,
  itemLabel,
}: {
  kind: CatalogKind
  title: string
  description: string
  icon: IconName
  itemLabel: string
}) => {
  const { project, isManager } = useProject()
  const { data, error, loading, reload, setData } = useAsync(() => api.fetchCatalog(project.id), [project.id])
  const [adding, setAdding] = useState(false)
  const [label, setLabel] = useState('')
  const [editing, setEditing] = useState<{ id: string; label: string } | null>(null)
  const { busy, run } = useAction()
  const confirm = useConfirm()

  const items = (data ?? []).filter((item) => item.kind === kind).sort((a, b) => a.sortOrder - b.sortOrder)

  const replace = (item: CatalogItem) => setData((current) => current?.map((entry) => (entry.id === item.id ? item : entry)))

  const add = async (event: FormEvent) => {
    event.preventDefault()
    const item = await run('add', () => api.addCatalogItem(project.id, kind, label.trim()), t('تمت الإضافة', 'Added'))
    if (item) {
      setData((current) => [...(current ?? []), item])
      setLabel('')
      setAdding(false)
    }
  }

  const move = async (item: CatalogItem, direction: -1 | 1) => {
    const index = items.indexOf(item)
    const other = items[index + direction]
    if (!other) return
    const [a, b] = await Promise.all([
      run(`mv${item.id}`, () => api.updateCatalogItem(item.id, { sortOrder: other.sortOrder })),
      run(`mv${other.id}`, () => api.updateCatalogItem(other.id, { sortOrder: item.sortOrder })),
    ])
    if (a) replace(a)
    if (b) replace(b)
  }

  const rename = async (event: FormEvent) => {
    event.preventDefault()
    if (!editing) return
    const item = await run('rename', () => api.updateCatalogItem(editing.id, { label: editing.label.trim() }), t('تم التعديل', 'Updated'))
    if (item) {
      replace(item)
      setEditing(null)
    }
  }

  const remove = async (item: CatalogItem) => {
    if (!(await confirm(t(`حذف «${item.label}» من القائمة؟ لن تتأثر التقارير السابقة.`, `Delete "${item.label}" from the list? Earlier reports are not affected.`), { confirmLabel: t('حذف', 'Delete') }))) return
    const done = await run(`rm${item.id}`, () => api.removeCatalogItem(item.id), t('تم الحذف', 'Deleted'))
    if (done) setData((current) => current?.filter((entry) => entry.id !== item.id))
  }

  const addButton = (
    <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
      {t(`إضافة ${itemLabel}`, `Add ${itemLabel}`)}
    </Button>
  )

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{title}</h1>
          <p className="muted">{description}</p>
        </div>
        {isManager ? addButton : null}
      </div>

      <Card>
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : !items.length ? (
          <EmptyState icon={icon} title={t(`لا توجد عناصر في ${title}`, `No items in ${title}`)}>
            {isManager ? addButton : null}
          </EmptyState>
        ) : (
          <ul className="catalog">
            {items.map((item, index) => (
              <li key={item.id}>
                <span className="catalog__label">{item.label}</span>
                {isManager ? (
                  <span className="catalog__actions">
                    <IconButton
                      icon="chevronRight"
                      label={t('نقل للأعلى', 'Move up')}
                      disabled={index === 0}
                      onClick={() => move(item, -1)}
                      className="rot-90"
                    />
                    <IconButton
                      icon="chevronLeft"
                      label={t('نقل للأسفل', 'Move down')}
                      disabled={index === items.length - 1}
                      onClick={() => move(item, 1)}
                      className="rot-90"
                    />
                    <IconButton icon="edit" label={t(`تعديل ${item.label}`, `Edit ${item.label}`)} onClick={() => setEditing({ id: item.id, label: item.label })} />
                    <IconButton icon="trash" label={t(`حذف ${item.label}`, `Delete ${item.label}`)} onClick={() => remove(item)} disabled={busy === `rm${item.id}`} />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {adding ? (
        <Sheet
          title={t(`إضافة ${itemLabel}`, `Add ${itemLabel}`)}
          onClose={() => setAdding(false)}
          footer={
            <>
              <Button onClick={() => setAdding(false)}>{t('إلغاء', 'Cancel')}</Button>
              <Button type="submit" form="catalog-add-form" variant="primary" busy={busy === 'add'} disabled={!label.trim()}>
                {t('إضافة', 'Add')}
              </Button>
            </>
          }
        >
          <form id="catalog-add-form" className="stack" onSubmit={add}>
            <TextField label={t(`اسم ${itemLabel}`, `${itemLabel} name`)} value={label} onChange={(event) => setLabel(event.target.value)} autoFocus />
          </form>
        </Sheet>
      ) : null}

      {editing ? (
        <Modal title={t(`تعديل ${itemLabel}`, `Edit ${itemLabel}`)} onClose={() => setEditing(null)}>
          <form onSubmit={rename} className="stack">
            <TextField label={t('الاسم', 'Name')} value={editing.label} onChange={(event) => setEditing({ ...editing, label: event.target.value })} />
            <div className="form-actions">
              <Button onClick={() => setEditing(null)}>{t('إلغاء', 'Cancel')}</Button>
              <Button type="submit" variant="primary" busy={busy === 'rename'} disabled={!editing.label.trim()}>
                {t('حفظ', 'Save')}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  )
}
